import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChartBar, ArrowsHorizontal, Sparkle, Info } from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * Broker Brackets — surfaces the AI Negotiator's proven floor + rejection
 * ceiling per broker on /app/loadboard/analytics.
 *
 * The visual bracket bar shows a broker's floor (won $/mi) and ceiling
 * (rejected $/mi) side-by-side, so pilots can spot underquoting patterns
 * before opening a Draft Bid modal.
 *
 * Auto-hides when the user has no qualifying brokers yet
 * (needs ≥1 win OR ≥2 rejections in the lookback window).
 */
export default function BrokerBracketsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("widest");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    client.get(`/loadboard/analytics/broker-brackets?limit=10&sort=${sort}`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && toast.error(e?.response?.data?.detail || "Broker brackets failed"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [sort]);

  // Range used to scale the visual bar — pinned to the global min/max across all rows
  const range = useMemo(() => {
    if (!data?.brokers?.length) return { min: 1.5, max: 3.5 };
    const vals = [];
    for (const b of data.brokers) {
      if (b.floor_per_mile) vals.push(b.floor_per_mile);
      if (b.ceiling_per_mile) vals.push(b.ceiling_per_mile);
      if (b.best_won_per_mile) vals.push(b.best_won_per_mile);
    }
    if (!vals.length) return { min: 1.5, max: 3.5 };
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    // Add 8% padding to each side so the extremes don't hug the bar edges
    const pad = Math.max(0.2, (hi - lo) * 0.08);
    return { min: Math.max(0.5, lo - pad), max: hi + pad };
  }, [data]);

  if (loading) {
    return (
      <div className="card-surface p-6" data-testid="brackets-card-loading">
        <div className="animate-pulse text-neutral-500 text-sm">Loading broker brackets…</div>
      </div>
    );
  }
  if (!data || !data.brokers?.length) return null;

  return (
    <div className="card-surface p-6" data-testid="brackets-card">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-indigo-300 mb-1">
            <ArrowsHorizontal size={13} weight="bold" />
            AI NEGOTIATION BRACKETS
          </div>
          <h3 className="font-display text-lg font-semibold text-white">
            Proven floor & rejection ceiling per broker
          </h3>
          <p className="text-[11px] text-neutral-500 mt-1 max-w-lg">
            Same math the <span className="text-indigo-300">AI Negotiator</span> uses at draft time.{" "}
            Median of prior wins = <span className="text-emerald-300">floor</span>;{" "}
            25th percentile of rejections = <span className="text-rose-300">ceiling</span>.{" "}
            The wider the bracket, the more room the AI has to move the anchor.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="inline-flex items-center gap-1.5 text-[11px] text-neutral-400">
            <ChartBar size={12} />
            {data.total_qualified} broker{data.total_qualified === 1 ? "" : "s"} qualified
          </div>
          {/* Sort dropdown */}
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="brackets-sort"
            className="bg-white/5 border border-white/10 rounded-lg text-xs text-white py-1.5 px-2 focus:border-[#FF5A1F] outline-none cursor-pointer"
          >
            <option value="widest">Widest bracket</option>
            <option value="recent">Most recent</option>
            <option value="win_rate">Win rate</option>
          </select>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 text-[10px] uppercase tracking-widest text-neutral-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-400" /> Floor (won)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-rose-400" /> Ceiling (rejected)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Sparkle size={9} weight="fill" className="text-amber-300" /> Best win
        </span>
      </div>

      <div className="space-y-3" data-testid="brackets-list">
        {data.brokers.map((b) => (
          <BracketRow key={b.broker_company} broker={b} range={range} />
        ))}
      </div>

      <div className="mt-4 text-[11px] text-neutral-500 flex items-start gap-1.5">
        <Info size={11} weight="regular" className="mt-0.5 shrink-0" />
        <span>
          Brokers appear here after ≥1 win OR ≥2 rejections in the last {data.lookback_days} days.
          Wider brackets mean more negotiation surface area — the AI targets the middle of the range
          adjusted for load specifics.
        </span>
      </div>
    </div>
  );
}


function BracketRow({ broker, range }) {
  const floor = broker.floor_per_mile;
  const ceiling = broker.ceiling_per_mile;
  const best = broker.best_won_per_mile;
  const wr = broker.win_rate_pct;

  // Map $ to % across the visible range so all rows share the same axis
  const pct = (val) => {
    if (val == null) return null;
    const span = Math.max(0.01, range.max - range.min);
    return Math.max(0, Math.min(100, ((val - range.min) / span) * 100));
  };
  const floorPct = pct(floor);
  const ceilingPct = pct(ceiling);
  const bestPct = pct(best);

  // Win-rate tint
  const wrTone = wr === null
    ? "text-neutral-500 bg-white/[0.02] border-white/10"
    : wr >= 75
      ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
      : wr >= 40
        ? "text-[#FF5A1F] bg-[#FF5A1F]/10 border-[#FF5A1F]/30"
        : "text-amber-400 bg-amber-500/10 border-amber-500/30";

  const brokerTestId = `bracket-row-${broker.broker_company.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className="py-3 px-3 rounded-lg hover:bg-white/[0.02] transition-colors" data-testid={brokerTestId}>
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm text-white font-medium truncate">{broker.broker_company}</div>
          <div className="text-[11px] text-neutral-500 mt-0.5">
            {broker.total_bids} bid{broker.total_bids === 1 ? "" : "s"}
            {" · "}{broker.wins_count}W {broker.rejects_count}L
            {broker.no_response_count > 0 && ` (${broker.no_response_count} ghosted)`}
            {broker.pending_count > 0 && ` · ${broker.pending_count} pending`}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {broker.bracket_pct != null && (
            <span className="inline-flex items-center rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-300 px-2 py-0.5 text-[10px] font-semibold tabular-nums">
              {broker.bracket_pct}% range
            </span>
          )}
          <div className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold tabular-nums ${wrTone}`}>
            {wr !== null ? `${wr}% win` : "—"}
          </div>
        </div>
      </div>

      {/* The visual bracket bar */}
      <div className="relative h-8 rounded-md bg-white/[0.03] border border-white/5 overflow-hidden">
        {/* Bracket fill — from floor to ceiling */}
        {floorPct != null && ceilingPct != null && ceilingPct > floorPct && (
          <div
            className="absolute top-0 bottom-0 bg-gradient-to-r from-emerald-500/25 via-indigo-500/20 to-rose-500/25"
            style={{ left: `${floorPct}%`, width: `${ceilingPct - floorPct}%` }}
          />
        )}
        {/* Floor marker */}
        {floorPct != null && (
          <div
            className="absolute top-1 bottom-1 w-[3px] bg-emerald-400 rounded-sm shadow-[0_0_6px_rgba(52,211,153,0.6)]"
            style={{ left: `calc(${floorPct}% - 1.5px)` }}
            title={`Floor: $${floor.toFixed(2)}/mi`}
          />
        )}
        {/* Ceiling marker */}
        {ceilingPct != null && (
          <div
            className="absolute top-1 bottom-1 w-[3px] bg-rose-400 rounded-sm shadow-[0_0_6px_rgba(251,113,133,0.6)]"
            style={{ left: `calc(${ceilingPct}% - 1.5px)` }}
            title={`Ceiling: $${ceiling.toFixed(2)}/mi`}
          />
        )}
        {/* Best-win star pin (only when it's distinct from floor) */}
        {bestPct != null && best !== floor && (
          <div
            className="absolute top-1/2 -translate-y-1/2 text-amber-300"
            style={{ left: `calc(${bestPct}% - 5px)` }}
            title={`Best win: $${best.toFixed(2)}/mi`}
          >
            <Sparkle size={11} weight="fill" />
          </div>
        )}
      </div>

      {/* Numbers row */}
      <div className="flex items-center justify-between mt-2 text-[11px] tabular-nums">
        <div className="flex items-center gap-3">
          {floor != null ? (
            <span className="text-emerald-300">Floor <b>${floor.toFixed(2)}/mi</b></span>
          ) : (
            <span className="text-neutral-600">No floor yet</span>
          )}
          {ceiling != null ? (
            <span className="text-rose-300">Ceiling <b>${ceiling.toFixed(2)}/mi</b></span>
          ) : (
            <span className="text-neutral-600">No ceiling yet</span>
          )}
        </div>
        {best != null && (
          <span className="text-amber-300">Best <b>${best.toFixed(2)}/mi</b></span>
        )}
      </div>
    </div>
  );
}
